import { useDeleteSpouses } from 'common/queries-and-mutations/spouse';
import Badge from 'components/Badge';
import withCreatePortal from 'components/HOC/withCreatePortal';
import { Icon, Icons } from 'components/Icon';
import { Loader } from 'components/Loader';
import ConfirmModal from 'components/Modal/ConfirmModal';
import TableActionWidget from 'components/TableWidget/TableActionWidget';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spouse, Spouses } from 'service/dto/spouse.dto';
import { StringFormater } from 'utils';
import EditSpouseModal from './EditSpouseModal';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';

type Props = {
  spouses: Spouses | undefined;
  onSuccess: () => void;
};

const EnhancedEditSpouseForm = withCreatePortal(EditSpouseModal);
const EnhancedConfirmModal = withCreatePortal(ConfirmModal);

export default function SpouseTable({ spouses, onSuccess }: Props) {
  const [selectedId, setSelectedId] = useState<null | number>(null);
  const [selectedUser, setSelectedUser] = useState<null | Spouse>(null);
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();

  const {
    mutate: deleteSpouse,
    isLoading: isDeleting,
    isSuccess: isDeleted
  } = useDeleteSpouses();

  useEffect(() => {
    if (isDeleted) {
      toast(<Alert message="Spouse Successfully Deleted" type="success" />);
      onSuccess();
      setSelectedUser(null);
      setShowAskToConfirm(false);
    }
  }, [isDeleting, isDeleted]);

  const onYesClick = () => {
    if (selectedUser?.id) {
      deleteSpouse(selectedUser?.id);
    } else {
      toast(
        <Alert
          message="Something went wrong, Please refresh this page and try again"
          type="error"
        />
      );
    }
  };

  return (
    <div>
      <table className="table-to-print w-full px-6 py-4 text-left text-sm">
        <thead className="border-b bg-white text-xs text-[#1F4173]">
          <tr>
            <th scope="col" className="px-6 py-4">
              Name
            </th>
            <th scope="col" className="px-6 py-4">
              Email
            </th>
            <th scope="col" className="px-6 py-4">
              Phone Number
            </th>
            <th scope="col" className="px-6 py-4">
              Status
            </th>
            <th scope="col" className="column-to-hide px-6 py-4">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="px-6 py-4">
          {!spouses?.length ? (
            <tr className="text-center">
              <td colSpan={5} className="py-10 text-lg">
                No results found
              </td>
            </tr>
          ) : (
            spouses?.map(spouse => (
              <tr
                key={spouse.id}
                className="border-b bg-white hover:bg-gray-50"
              >
                <td className="px-6 py-4 text-xs font-normal  text-[#353535]">
                  {StringFormater(
                    `${spouse.spouseFirstName} ${spouse.spouseMiddleName} ${spouse.spouseLastName}`
                  )}
                </td>
                <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                  {spouse.emailAddress || 'N/A'}
                </td>
                <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                  {spouse.phoneNumber || 'N/A'}
                </td>
                <td className="px-6 py-4">
                  <Badge
                    title={spouse.isAssigned ? 'Assigned' : 'Unassigned'}
                    type={spouse.isAssigned ? 'success' : 'danger'}
                    size="sm"
                  />
                </td>
                <td className="column-to-hide px-6 py-4">
                  <button
                    type="button"
                    role="button"
                    tabIndex={-1}
                    onKeyDown={() => {
                      setSelectedId(spouse.id);
                    }}
                    onClick={() => {
                      setSelectedId(spouse.id);
                    }}
                  >
                    <Icon name={Icons.MoreMenu} />
                  </button>
                  {selectedId === spouse.id && isDeleting && (
                    <Loader height={20} width={20} type={'Wait'} />
                  )}
                  {selectedId === spouse.id && (
                    <TableActionWidget
                      onViewClick={() =>
                        navigate(`spouse-profile/${spouse.id}`)
                      }
                      onEditClick={() => {
                        setShowEditModal(true);
                        setSelectedUser(spouse);
                      }}
                      onRemoveClick={() => {
                        setShowAskToConfirm(true);
                        setSelectedUser(spouse);
                      }}
                      onDismiss={() => setSelectedId(null)}
                    />
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {showAskToConfirm && selectedUser && (
        <EnhancedConfirmModal
          content="Are you sure you want to remove this spouse?"
          onConfirm={onYesClick}
          onCancel={() => {
            setShowAskToConfirm(false);
            setSelectedUser(null);
          }}
          actionText="Yes, Remove"
          cancelText="No"
          title="Action Required"
          isSubmitting={isDeleting}
        />
      )}
      {showEditModal && selectedUser && (
        <EnhancedEditSpouseForm
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedUser(null);
            onSuccess();
          }}
          spouse={selectedUser}
        />
      )}
    </div>
  );
}
